import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { MediumText } from "./styles";

const GlowingSpheresAnimation = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    // Fixed dimensions
    const width = 600;
    const height = 600;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf9fbfc);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 18;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);

    // Clean container
    while (mountRef.current.firstChild) {
      mountRef.current.removeChild(mountRef.current.firstChild);
    }
    mountRef.current.appendChild(renderer.domElement);

    // Create materials
    const orangeMaterial = new THREE.MeshStandardMaterial({
      color: 0x444444,
      roughness: 0.7,
      metalness: 0.1,
      emissive: 0xff6d1d, // Orange emissive color
      emissiveIntensity: 0.1,
    });

    const centerMaterial = new THREE.MeshStandardMaterial({
      color: 0x444444,
      roughness: 0.7,
      metalness: 0.2,
      emissive: 0xff6d1d,
      emissiveIntensity: 0.0,
    });

    // Increase spacing for the corner spheres and set positions.
    const spacing = 10; // Increased from 6 to 10
    const cornerPositions = [
      { x: -spacing / 2, y: spacing / 2, z: 0 }, // Top-left
      { x: spacing / 2, y: spacing / 2, z: 0 }, // Top-right
      { x: -spacing / 2, y: -spacing / 2, z: 0 }, // Bottom-left
      { x: spacing / 2, y: -spacing / 2, z: 0 }, // Bottom-right
    ];
    const centerPosition = { x: 0, y: 0, z: 0 };

    // Create corner spheres
    const cornerSpheres = [];
    cornerPositions.forEach((pos, index) => {
      const material = orangeMaterial.clone();
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(1, 32, 32),
        material,
      );
      sphere.position.set(pos.x, pos.y, pos.z);
      sphere.userData = {
        originalPosition: { ...pos },
        // Used only for color changes.
        targetEmissiveIntensity: index === 0 ? 0.5 : 0.1,
        targetScale: 1.0,
        fadeProgress: 1.0, // Already full size initially
        fading: false, // Not fading at the start
        visible: true, // Track visibility state
      };
      scene.add(sphere);
      cornerSpheres.push(sphere);
    });

    // Create center sphere
    const centerSphere = new THREE.Mesh(
      new THREE.SphereGeometry(1, 32, 32),
      centerMaterial,
    );
    centerSphere.position.set(
      centerPosition.x,
      centerPosition.y,
      centerPosition.z,
    );
    centerSphere.userData = {
      baseScale: 0.5,
      targetBaseScale: 0.5,
      targetEmissiveIntensity: 0.2,
      absorbedCount: 0, // Track how many spheres have been absorbed
    };
    centerSphere.scale.set(0.5, 0.5, 0.5);
    scene.add(centerSphere);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 10, 10);
    scene.add(directionalLight);

    // Animation control variables.
    let animationProgress = 0;
    const animationDuration = 45; // Slowed down from 30 to 45
    let pauseCounter = 15;
    let currentCornerIndex = 0;
    let state = "PULSATE"; // States: PULSATE, FEED, GROW, WAIT, RESET_ALL
    const fadeDuration = 1.5; // seconds for fade to complete (increased from 1.0)
    let fullCycleComplete = false;

    const clock = new THREE.Clock();

    // Easing helper function.
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // Function to reset all spheres
    const resetAllSpheres = () => {
      // Reset all corner spheres
      cornerSpheres.forEach((sphere, index) => {
        sphere.visible = true;
        sphere.userData.visible = true;
        sphere.userData.targetEmissiveIntensity = index === 0 ? 0.5 : 0.1;
        sphere.userData.targetScale = 1.0;
        sphere.userData.fadeProgress = 0;
        sphere.userData.fading = true;
        sphere.scale.set(0, 0, 0); // Start small
        sphere.position.set(
          sphere.userData.originalPosition.x,
          sphere.userData.originalPosition.y,
          sphere.userData.originalPosition.z,
        );
      });

      // Reset center sphere
      centerSphere.userData.targetBaseScale = 0.5;
      centerSphere.userData.absorbedCount = 0;
      centerSphere.userData.targetEmissiveIntensity = 0.2;

      currentCornerIndex = 0;
      state = "PULSATE";
      pauseCounter = 15;
      fullCycleComplete = false;
    };

    const animate = () => {
      const deltaTime = clock.getDelta();
      const animationId = requestAnimationFrame(animate);

      // Update corner spheres: update emissive intensity, color, and scale.
      cornerSpheres.forEach((sphere) => {
        // Only update if the sphere is visible
        if (sphere.visible) {
          sphere.material.emissiveIntensity = THREE.MathUtils.lerp(
            sphere.material.emissiveIntensity,
            sphere.userData.targetEmissiveIntensity,
            0.1,
          );
          const emissionFactor = THREE.MathUtils.clamp(
            sphere.material.emissiveIntensity / 0.6,
            0,
            1,
          );
          sphere.material.color.lerpColors(
            new THREE.Color(0x444444),
            new THREE.Color(0xff6d1d),
            emissionFactor,
          );
          let scaleVal = sphere.userData.targetScale;
          if (sphere.userData.fading) {
            scaleVal = THREE.MathUtils.lerp(
              sphere.userData.startFadeScale || 0,
              sphere.userData.targetScale,
              sphere.userData.fadeProgress,
            );
          }
          sphere.scale.set(scaleVal, scaleVal, scaleVal);
        }
      });

      // Update center sphere: smoothly grow in size and update color.
      centerSphere.material.emissiveIntensity = THREE.MathUtils.lerp(
        centerSphere.material.emissiveIntensity,
        centerSphere.userData.targetEmissiveIntensity,
        0.1,
      );
      const centerEmissionFactor = THREE.MathUtils.clamp(
        centerSphere.material.emissiveIntensity / 0.8,
        0,
        1,
      );
      centerSphere.material.color.lerpColors(
        new THREE.Color(0x444444),
        new THREE.Color(0xff6d1d),
        centerEmissionFactor,
      );
      centerSphere.userData.baseScale = THREE.MathUtils.lerp(
        centerSphere.userData.baseScale,
        centerSphere.userData.targetBaseScale,
        0.05,
      );
      centerSphere.scale.set(
        centerSphere.userData.baseScale,
        centerSphere.userData.baseScale,
        centerSphere.userData.baseScale,
      );

      // State machine handling.
      switch (state) {
        case "PULSATE":
          // Update active corner sphere's emissive intensity.
          cornerSpheres.forEach((sphere, index) => {
            if (index === currentCornerIndex && sphere.visible) {
              sphere.userData.targetEmissiveIntensity =
                0.5 + Math.sin(Date.now() * 0.003) * 0.1; // Even slower pulsation
            } else if (sphere.visible) {
              sphere.userData.targetEmissiveIntensity = 0.1;
            }
          });
          pauseCounter--;
          if (pauseCounter <= 0) {
            state = "FEED";
            animationProgress = 0;
          }
          break;

        case "FEED": {
          const sphere = cornerSpheres[currentCornerIndex];
          const t = animationProgress / animationDuration;
          const easedT = easeInOutCubic(t);
          const startPos = sphere.userData.originalPosition;
          sphere.position.x =
            startPos.x + (centerPosition.x - startPos.x) * easedT;
          sphere.position.y =
            startPos.y + (centerPosition.y - startPos.y) * easedT;
          sphere.position.z = 2 * Math.sin(Math.PI * easedT);
          sphere.userData.targetScale = 1 - 0.8 * easedT;
          sphere.userData.targetEmissiveIntensity = 0.5 + easedT * 0.3;
          centerSphere.userData.targetEmissiveIntensity = 0.2 + easedT * 0.4;
          animationProgress++;
          if (animationProgress >= animationDuration) {
            state = "GROW";
            // Make the sphere invisible once it reaches the center
            sphere.visible = false;
            sphere.userData.visible = false;
          }
          break;
        }

        case "GROW": {
          // Make center sphere grow more visibly to show absorption
          centerSphere.userData.targetBaseScale += 0.25;
          centerSphere.userData.targetEmissiveIntensity = 0.8;
          centerSphere.userData.absorbedCount++; // Increment absorption count

          state = "WAIT";
          pauseCounter = 30; // Wait between absorptions
          break;
        }

        case "WAIT":
          centerSphere.userData.targetEmissiveIntensity *= 0.99;
          pauseCounter--;

          if (pauseCounter <= 0) {
            // If we've absorbed all spheres, wait longer before resetting
            if (centerSphere.userData.absorbedCount >= cornerSpheres.length) {
              fullCycleComplete = true;
              state = "RESET_ALL";
              pauseCounter = 90; // Longer pause before complete reset
            } else {
              // Move to the next sphere
              currentCornerIndex =
                (currentCornerIndex + 1) % cornerSpheres.length;
              state = "PULSATE";
              pauseCounter = 30;
            }
          }
          break;

        case "RESET_ALL":
          pauseCounter--;
          if (pauseCounter <= 0) {
            resetAllSpheres();
          }
          break;
      }

      // Update fade progress for spheres that are fading.
      cornerSpheres.forEach((sphere) => {
        if (sphere.userData.fading && sphere.visible) {
          sphere.userData.fadeProgress += deltaTime / fadeDuration;
          if (sphere.userData.fadeProgress > 1) {
            sphere.userData.fadeProgress = 1;
            sphere.userData.fading = false;
          }
        }
      });

      renderer.render(scene, camera);
      return animationId;
    };

    const animationId = animate();

    // Cleanup on unmount.
    return () => {
      cancelAnimationFrame(animationId);
      if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }

      cornerSpheres.forEach((sphere) => sphere.material.dispose());
      centerMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <>
      <div
        ref={mountRef}
        style={{
          width: "600px",
          height: "600px",
          margin: "0 auto",
        }}
      />
      <AnimatedConsolidating />
    </>
  );
};

function AnimatedConsolidating() {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const renderDots = () => {
    // Create a string with exactly 3 dots
    const dots = "...";

    // Return visible dots based on current count and invisible remaining dots
    return (
      <span>
        {dots.substring(0, dotCount)}
        <span className="opacity-0">{dots.substring(dotCount)}</span>
      </span>
    );
  };

  return (
    <MediumText style={{ textAlign: "center" }}>
      Consolidating{renderDots()}
    </MediumText>
  );
}

export default GlowingSpheresAnimation;
